import * as React from 'react';
import { useState, useCallback, useMemo, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import {
  Map,
  Marker,
  Popup,
  NavigationControl,
  FullscreenControl,
  ScaleControl,
  GeolocateControl,
  Source,
  Layer,
  LogoControl,
  MapProvider,
  useControl,
  useMap,
  AttributionControl
} from 'react-map-gl/maplibre';
import { Modal, Button } from 'react-bootstrap';
import goldfinderstyle from './map-gl/goldfinder-style.json';
import '@geoman-io/maplibre-geoman-free/dist/maplibre-geoman.css';

import { TerraDrawPointMode, TerraDrawRectangleMode, TerraDrawCircleMode } from 'terra-draw';

// Import optimized overlay components
import { TenementOverlayMapLibreOptimized } from './tenements/TenementOverlayOptimized';
import { AmalgamationsPendingOverlay } from './tenements/AmalgamationsPendingOverlay';
import { AssayOverlayMapLibreOptimized } from './soil-assay/AssayOverlayOptimized';
import { MinedexOverlayMapLibre } from './minedex/MinedexOverlay';
  import { DrillHoleOverlayMapLibreOptimized } from './drill-hole/DrillHoleOverlayOptimized'
  import { GraticlesOverlayLibre } from './graticles/GraticlesOverlay';
import { Section19OverlayLibre } from './Section19/Section19Overlay';

// Import utilities
import { wfsCache, debounce, requestManager } from '../utils/wfsCache';
import { SideBar } from './map-gl/SideBar';
import { SideBar40E } from './map-gl/SideBar40E';
import { SampleReporter } from './soil-assay/SampleReporter';
import { DHSampleReporter } from './drill-hole/DHSampleReporter';

// Import existing components and utilities
import { getDefaultPreset, DEFAULT_PRESET_ID } from './map-gl/presets/defaultPreset';
import { buildPresetPayloadFromCurrentState, deepClone } from './map-gl/presets/presetSerialization';
import { useOverlayPresets } from '../hooks/useOverlayPresets';
import { PresetsControl } from './map-gl/PresetsControl';

import * as turf from "@turf/turf";
import UTMLatLng from 'utm-latlng';
import { GeocodingControl } from "@maptiler/geocoding-control/react";
import { createMapLibreGlMapController } from "@maptiler/geocoding-control/maplibregl-controller";
import maplibregl from 'maplibre-gl';
import "@maptiler/geocoding-control/style.css";
import { Collapse } from 'react-bootstrap';
import { CreateDrawPointPopup, DrawControl } from './map-gl/DrawControl';
import { MaplibreTerradrawControl } from '@watergis/maplibre-gl-terradraw';
import { AlertList } from "react-bs-notifier";
import classNames from 'classnames';
import { useMapViewPersistence } from '../hooks/useMapViewPersistence';
import { FloatingMenu, SidebarToggle, MobileSidebarToggle } from './map-gl/FloatingMenu';

// Initial view state is now provided by useMapViewPersistence hook

export const emptyGeoJsonGraticles = {
  "type": "FeatureCollection",
  "name": "graticules",
  "crs": { "type": "name", "properties": { "name": "urn:ogc:def:crs:EPSG::4283" } },
  "features": []
};

/**
 * Optimized MapGL component with improved performance for WFS queries
 */
export default function MapGlOptimized({
  isSideBarShown,
  mobileFiltersOpen,
  setSideBarShown,
  onToggleSidebar,
  onOpenAccountSettings,
  onMobileFiltersToggle
}) {
  // Map view persistence - restores user's last viewport position
  const { initialViewState, isLoading: isViewportLoading, saveViewport } = useMapViewPersistence();

  const [alerts, setAlerts] = React.useState([]);

  const onDismissed = useCallback(alert => {
    setAlerts(alerts => {
      const idx = alerts.indexOf(alert);
      if (idx < 0) return alerts;
      return [...alerts.slice(0, idx), ...alerts.slice(idx + 1)];
    });
  }, []);

  // State management — initializers derive from the single source of truth
  // in `getDefaultPreset()`. This ensures the hardcoded defaults here and the
  // built-in "Default" preset can never drift apart when new fields are added.
  const [tenementFilters, setTenementFilters] = useState(() => getDefaultPreset().tenementFilters);
  const [minedexFilters, setMinedexFilters] = useState(() => getDefaultPreset().minedexFilters);
  const [assayFilters, setAssayFilters] = useState(() => getDefaultPreset().assayFilters);
  const [drillHolefilters, setDrillHoleFilters] = useState(() => getDefaultPreset().drillHoleFilters);

  const emptyGeoJson = {
    "type": "FeatureCollection",
    "name": "Mining_Tenements_DMIRS_003",
    "crs": { "type": "name", "properties": { "name": "urn:ogc:def:crs:EPSG::4283" } },
    "features": []
  };

  // Data state
  const [tenementGeoJson, setTenementGeojson] = useState(emptyGeoJson);
  const [minedexGeoJson, setMinedexGeoJson] = useState(emptyGeoJson);
  const [assayGeoJson, setAssayGeojson] = useState(emptyGeoJson);
  const [graticleGeoJson, setGraticleGeoJson] = useState(emptyGeoJson);
  const [section19GeoJson, setSection19GeoJson] = useState(emptyGeoJson);
  const [drillHoleGeoJson, setDrillHoleGeoJson] = useState(emptyGeoJson);
  const [amalgamationsPendingGeoJson, setAmalgamationsPendingGeoJson] = useState(emptyGeoJson);

  // Popup state
  const [tenementPopup, setTenementPopup] = useState(<></>);
  const [showTenementPopup, setShowTenementPopup] = useState(false);
  const [selectedTenement, setSelectedTenement] = useState({ properties: { gid: 99999999 } });
  const [selectedAmalgamation, setSelectedAmalgamation] = useState({ properties: { OBJECTID: 99999999 } });
  const [assayPopup, setAssayPopup] = useState(<></>);
  const [showAssayPopup, setShowAssayPopup] = useState(false);
  const [reportAssayModalShow, setReportAssayModalShow] = useState(false);
  const [reportedAssay, setReportedAssay] = useState({ properties: {} });
  const [minedexPopup, setMinedexPopup] = useState(<></>);
  const [showMinedexPopup, setShowMinedexPopup] = useState(false);
  const [graticlePopup, setGraticlePopup] = useState(<></>);
  const [showGraticlePopup, setShowGraticlePopup] = useState(false);
  const [section19Popup, setSection19Popup] = useState(<></>);
  const [showSection19Popup, setShowSection19Popup] = useState(false);
  const [drillHolePopup, setDrillHolePopup] = useState(<></>);
  const [reportDhModalShow, setReportDhModalShow] = useState(false);
  const [reportedDh, setReportedDh] = useState({ properties: {} });
  const [showDrillHolePopup, setShowDrillHolePopup] = useState(false);
  const [drawPointPopup, setDrawPointPopup] = useState(<></>);
  const [showDrawPointPopup, setShowDrawPointPopup] = useState(false);

  // Other state
  const [API_KEY] = useState('WCXZHw7CwlQPRosD6glL');
  const [mapController, setMapController] = useState();
  const [mousePoint, setMousePoint] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 992);

  // Track window resize for mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 992);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [is40ESideBarShown, setIs40ESideBarShown] = useState(false);
  const [selectedGraticleStack, setSelectedGraticuleStack] = useState(emptyGeoJsonGraticles);
  const [tenement40E, setTenement40E] = useState(null);
  const [show40EConfirmModal, setShow40EConfirmModal] = useState(false);
  const [pendingTenement40E, setPendingTenement40E] = useState(null);
  const [assayMarkerColourSettings, setAssayMarkerColourSettings] = useState(() => getDefaultPreset().assayMarkerColourSettings);
  const [drillHoleMarkerColourSettings, setDrillHoleMarkerColourSettings] = useState(() => getDefaultPreset().drillHoleMarkerColourSettings);

  // Layer visibility state — derived from getDefaultPreset() (single source of truth)
  const [layerVisibility, setLayerVisibility] = useState(() => getDefaultPreset().layerVisibility);
  const [overlayVisibility, setOverlayVisisbility] = useState(() => getDefaultPreset().overlayVisibility);

  // Overlay presets — per-user saved configurations
  const {
    presets,
    activePresetId,
    setActivePresetId,
    isLoading: isPresetsLoading,
    savePresetAsNew,
    updatePreset,
    renamePreset,
    deletePreset,
    getPresetById,
    canSaveMore,
    userLoggedIn: presetsUserLoggedIn,
    userPresetCount,
    maxUserPresets,
  } = useOverlayPresets();

  // Apply preset payload to local state whenever the active preset changes.
  // Waits for presets to finish loading so a restored localStorage id can
  // actually resolve to a preset entry.
  useEffect(() => {
    if (isPresetsLoading) return;
    let payload;
    if (activePresetId === DEFAULT_PRESET_ID) {
      payload = getDefaultPreset();
    } else {
      const entry = getPresetById(activePresetId);
      if (!entry) {
        // Active id pointed at a preset that no longer exists — fall back to Default.
        setActivePresetId(DEFAULT_PRESET_ID);
        return;
      }
      payload = entry.payload;
    }
    // Deep-clone before applying so local state never shares references with
    // the in-memory preset cache. Some editors (e.g. the marker colour pickers)
    // mutate elements in place, which would otherwise corrupt the stored preset.
    const applied = deepClone(payload);
    setOverlayVisisbility(applied.overlayVisibility);
    setLayerVisibility(applied.layerVisibility);
    setTenementFilters(applied.tenementFilters);
    setMinedexFilters(applied.minedexFilters);
    setAssayFilters(applied.assayFilters);
    setDrillHoleFilters(applied.drillHoleFilters);
    setAssayMarkerColourSettings(applied.assayMarkerColourSettings);
    setDrillHoleMarkerColourSettings(applied.drillHoleMarkerColourSettings);
  }, [activePresetId, isPresetsLoading, getPresetById, setActivePresetId]);

  const buildCurrentPayload = useCallback(
    () =>
      buildPresetPayloadFromCurrentState({
        overlayVisibility,
        layerVisibility,
        tenementFilters,
        minedexFilters,
        assayFilters,
        drillHoleFilters: drillHolefilters,
        assayMarkerColourSettings,
        drillHoleMarkerColourSettings,
      }),
    [
      overlayVisibility,
      layerVisibility,
      tenementFilters,
      minedexFilters,
      assayFilters,
      drillHolefilters,
      assayMarkerColourSettings,
      drillHoleMarkerColourSettings,
    ]
  );

  const onPresetError = useCallback(
    (err) => {
      setAlerts((prev) => [
        ...prev,
        {
          id: new Date().getTime(),
          type: 'danger',
          headline: 'Preset error',
          message: err?.message || 'An unexpected error occurred.',
        },
      ]);
    },
    []
  );

  // Drawing state
  const [drawpointMode, setDrawPointMode] = useState(new TerraDrawPointMode({
    styles: {
      pointWidth: 12,
      pointColor: '#0000FF',
      pointOutlineColor: '#FFFFFF',
      pointOutlineWidth: 3,
      editedPointWidth: 12,
      editedPointOutlineWidth: 3,
      editedPointColor: '#0000FF'
    }
  }));

  const [drawCtrl, setDrawctrl] = useState(new MaplibreTerradrawControl({
    modes: ['point', 'select', 'delete-selection', 'download'],
    open: true,
    modeOptions: {
      point: drawpointMode
    }
  }));

  var utm = new UTMLatLng();

  // Keep existing functions (trigger40EWizard, onMapClick, etc.)
  const trigger40EWizard = (tenement) => {
    setPendingTenement40E(tenement);
    setShow40EConfirmModal(true);
  };

  const confirm40EWizard = () => {
    setIs40ESideBarShown(true);
    setSideBarShown(false);
    setTenement40E(pendingTenement40E);
    setShowTenementPopup(false);
    setOverlayVisisbility({ ...overlayVisibility, graticles: true });
    
    const bounds = turf.bbox(pendingTenement40E);
    var wfsbbox = bounds.toString();
    import('./graticles/GraticlesOverlay').then(module =>
      module.queryGraticlesWFSLibreWithPolygonFilter(wfsbbox, setGraticleGeoJson, pendingTenement40E)
    );
    
    setShow40EConfirmModal(false);
    setPendingTenement40E(null);
  };

  const cancel40EWizard = () => {
    setShow40EConfirmModal(false);
    setPendingTenement40E(null);
  };

  // Import and use existing onMapClick function
  const onMapClick = useCallback(event => {
    if (is40ESideBarShown) {
      const selectedgraticle = event.features.filter(e => e.source === 'graticle')
      const selectedTenementsGrat = event.features.filter(e => (e.source === 'tenements') && (e.properties.fmt_tenid === tenement40E.properties.fmt_tenid))

      if ((selectedgraticle.length !== 0) && (selectedTenementsGrat.length != 0)) {
        const tempStack = emptyGeoJson;
        tempStack.features = selectedGraticleStack.features.filter(e =>
          !((e.properties.block_no === selectedgraticle[0].properties.block_no)
            && (e.properties.primary_no === selectedgraticle[0].properties.primary_no))
        )

        if (tempStack.features.length !== selectedGraticleStack.features.length) {
          setSelectedGraticuleStack(tempStack)
        } else if (tempStack.features.length < 10) {
          if (tempStack.features.length === 0) {
            tempStack.features.push(selectedgraticle[0])
            setSelectedGraticuleStack(tempStack)
            return;
          }

          if (tempStack.features.filter(e => {
            let matches = 0;
            selectedgraticle[0].geometry.coordinates[0].forEach(selected => {
              e.geometry.coordinates[0].forEach(compareTo => {
                if ((compareTo[0] === selected[0]) && (compareTo[1] === selected[1])) {
                  matches++;
                  return;
                }
              })
            });
            return matches > 1;
          }).length !== 0) {
            tempStack.features.push(selectedgraticle[0])
            setSelectedGraticuleStack(tempStack)
            return;
          } else {
            setAlerts(alerts => [
              ...alerts,
              {
                id: new Date().getTime(),
                type: 'danger',
                headline: `Selected Graticules must have shared sides`,
                message: 'Each graticule selected for a section 40E permit have a shared side'
              }
            ]);
          }
        } else {
          setAlerts(alerts => [
            ...alerts,
            {
              id: new Date().getTime(),
              type: 'danger',
              headline: `Maxiumum selected Graticules Reached (10)`,
              message: 'To select more, please de-select existing Graticules'
            }
          ]);
        }
        return;
      } else {
        setAlerts(alerts => [
          ...alerts,
          {
            id: new Date().getTime(),
            type: 'danger',
            headline: `Please select a Graticule within the selected Exploration Licence`,
            message: 'If you wish to close the Generator, press Cancel in the sidebar. '
          }
        ]);
        return;
      }
    }

    // Check if there is a marker there
    const selectedMarkers = drawCtrl.getTerraDrawInstance().getFeaturesAtLngLat(event.lngLat);
    if (selectedMarkers.length !== 0) {
      setShowDrawPointPopup(true);
      setDrawPointPopup(<CreateDrawPointPopup drawctrl={drawCtrl} feature={selectedMarkers[0]} latLng={event.lngLat} setClosed={setShowDrawPointPopup} />)
      return;
    }

    const selectedDh = event.features.filter(e => e.source === 'dhsamples')
    if (selectedDh.length !== 0) {
      setShowDrillHolePopup(true)
      // Import createSingleDrillHolePopupTable from drill-hole overlay
      import('./drill-hole/DrillHoleOverlay').then(module => {
        setDrillHolePopup(module.createSingleDrillHolePopupTable(selectedDh[0], event.lngLat, setShowDrillHolePopup, setReportDhModalShow, setReportedDh))
      })
      return;
    } else {
      setShowDrillHolePopup(false)
      setDrillHolePopup(<></>)
    }

    const selectedAssays = event.features.filter(e => e.source === 'soilsamples')
    if (selectedAssays.length !== 0) {
      setShowAssayPopup(true)
      // Import createSingleAssayPopupTable from soil-assay overlay
      import('./soil-assay/AssayOverlay').then(module => {
        setAssayPopup(module.createSingleAssayPopupTable(selectedAssays[0], event.lngLat, setShowAssayPopup, setReportAssayModalShow, setReportedAssay))
      })
      return;
    } else {
      setShowAssayPopup(false)
      setAssayPopup(<></>)
    }

    const selectedminedex = event.features.filter(e => e.source === 'minedex')
    if (selectedminedex.length !== 0) {
      // Clear tenement selections when clicking minedex
      setShowTenementPopup(false)
      setSelectedTenement({ properties: { gid: 99999999 } })
      setSelectedAmalgamation({ properties: { OBJECTID: 99999999 } })

      setShowMinedexPopup(true);
      import('./minedex/MinedexOverlay').then(module => {
        setMinedexPopup(module.createSingleMinedexPopupTable(selectedminedex[0], event.lngLat, setShowMinedexPopup))
      })
      return
    } else {
      setShowMinedexPopup(false)
      setMinedexPopup(<></>)
    }

    // Check for amalgamations pending tenements first (they render on top)
    const selectedAmalgamations = event.features.filter(e => e.source === 'amalgamations-pending')
    if (selectedAmalgamations.length !== 0) {
      // Toggle selection if clicking the same amalgamation
      if (selectedAmalgamation.properties.OBJECTID === selectedAmalgamations[0].properties.OBJECTID) {
        setShowTenementPopup(false)
        setSelectedAmalgamation({ properties: { OBJECTID: 99999999 } })
        setTenementPopup(<></>)
        return;
      }

      // Clear the old popup first to ensure React re-renders when we set the new one
      setTenementPopup(<></>)
      setSelectedAmalgamation(selectedAmalgamations[0])
      // Clear regular tenement selection
      setSelectedTenement({ properties: { gid: 99999999 } })
      import('./tenements/AmalgamationsPendingOverlay').then(module => {
        setTenementPopup(module.createAmalgamationsPendingPopup(selectedAmalgamations[0], event.lngLat, setShowTenementPopup))
        setShowTenementPopup(true)
      })
      return;
    }

    // Filter tenements and deduplicate by fmt_tenid (same tenement can appear from multiple layers)
    const allTenementFeatures = event.features.filter(e => e.source === 'tenements')
    const selectedTenements = allTenementFeatures
      .filter((feature, index, self) =>
        index === self.findIndex(f => f.properties.fmt_tenid === feature.properties.fmt_tenid)
      )

    // Debug logging for tenement selection issues
    const clickedIds = selectedTenements.map(f => f.properties.fmt_tenid).join(', ') || 'none'
    const clickedLayers = allTenementFeatures.map(f => f.layer?.id).join(', ') || 'none'
    console.log(`[Tenement Click] clicked: ${clickedIds} | layers: ${clickedLayers} | currentSelected: ${selectedTenement?.properties?.fmt_tenid || 'none'} (gid: ${selectedTenement?.properties?.gid})`)

    if (selectedTenements.length === 1) {
      const actualTenement = tenementGeoJson.features.filter(e => e.properties.fmt_tenid === selectedTenements[0].properties.fmt_tenid)

      // Safety check
      if (!actualTenement || actualTenement.length === 0) {
        console.warn('[Tenement Click] NOT FOUND in geoJson:', selectedTenements[0].properties.fmt_tenid)
        return;
      }

      // Toggle selection if clicking the same tenement
      if (selectedTenement.properties.gid === actualTenement[0].properties.gid) {
        console.log('[Tenement Click] TOGGLE OFF:', actualTenement[0].properties.fmt_tenid)
        setShowTenementPopup(false)
        setSelectedTenement({ properties: { gid: 99999999 } })
        setTenementPopup(<></>)
        return;
      }

      console.log('[Tenement Click] SELECT:', actualTenement[0].properties.fmt_tenid)
      // Clear the old popup first to ensure React re-renders when we set the new one
      setTenementPopup(<></>)
      setSelectedTenement(actualTenement[0])
      // Clear amalgamation selection
      setSelectedAmalgamation({ properties: { OBJECTID: 99999999 } })
      // Import createSingleTenementPopupTable from tenements overlay
      import('./tenements/TenementOverlay').then(module => {
        setTenementPopup(module.createSingleTenementPopupTable(actualTenement[0], event.lngLat, setShowTenementPopup, trigger40EWizard))
        setShowTenementPopup(true)
      })
      return;
    } else if (selectedTenements.length > 1) {
      console.log('[Tenement Click] MULTI-SELECT popup for:', selectedTenements.map(f => f.properties.fmt_tenid).join(', '))
      setShowTenementPopup(true)
      // Clear amalgamation selection
      setSelectedAmalgamation({ properties: { OBJECTID: 99999999 } })
      setTenementPopup(<Popup latitude={event.lngLat.lat} longitude={event.lngLat.lng}
        anchor="bottom"
        onClose={() => {
          setShowTenementPopup(false)
          setSelectedTenement({ properties: { gid: 99999999 } })
        }}>{
          selectedTenements.map((feature) => {
            const actualTenement = tenementGeoJson.features.filter(e => e.properties.fmt_tenid === feature.properties.fmt_tenid)
            // Safety check - skip if tenement not found in geoJson
            if (!actualTenement || actualTenement.length === 0) {
              return null
            }
            return <p key={feature.properties.fmt_tenid}><a href="#" onClick={() => {
              setSelectedTenement(actualTenement[0])
              // Clear amalgamation selection when clicking multiple tenements list
              setSelectedAmalgamation({ properties: { OBJECTID: 99999999 } })
              import('./tenements/TenementOverlay').then(module => {
                setTenementPopup(module.createSingleTenementPopupTable(actualTenement[0], event.lngLat, setShowTenementPopup, trigger40EWizard))
              })
            }}>
              {actualTenement[0].properties.fmt_tenid}
            </a></p>
          }).filter(Boolean)
        }
      </Popup>)
      return;
    }

    // If we reach here, user clicked on empty space - clear all tenement selections
    if (showTenementPopup && selectedTenements.length === 0) {
      console.log('[Tenement Click Debug] Clearing selection - no tenements detected at click location')
      setShowTenementPopup(false)
      setSelectedTenement({ properties: { gid: 99999999 } })
      setSelectedAmalgamation({ properties: { OBJECTID: 99999999 } })
      setTenementPopup(<></>)
    }

    const selectedgraticle = event.features.filter(e => e.source === 'graticle')
    if (selectedgraticle.length !== 0) {
      setShowGraticlePopup(true);
      import('./graticles/GraticlesOverlay').then(module => {
        setGraticlePopup(module.createSingleGraticlePopupTable(selectedgraticle[0], event.lngLat, setShowGraticlePopup))
      })
    } else {
      setShowGraticlePopup(false)
      setGraticlePopup(<></>)
    }

    const selectedsection19 = event.features.filter(e => e.source === 'section19')
    if (selectedsection19.length !== 0) {
      setShowSection19Popup(true);
      import('./Section19/Section19Overlay').then(module => {
        setSection19Popup(module.createSingleSection19PopupTable(selectedsection19[0], event.lngLat, setShowSection19Popup))
      })
    } else {
      setShowSection19Popup(false)
      setSection19Popup(<></>)
    }
  }, [is40ESideBarShown, selectedGraticleStack, tenementGeoJson, tenement40E, drawCtrl, selectedTenement, showTenementPopup, selectedAmalgamation]);

  // Optimized handleMapMoveEnd with debouncing and caching
  const handleMapMoveEnd = useCallback(debounce((e) => {
    const bounds = e.target.getBounds();
    const wfsbbox = [bounds.getSouthWest().lng, bounds.getSouthWest().lat, bounds.getNorthEast().lng, bounds.getNorthEast().lat].join();
    const zoom = e.target.getZoom();
    const center = e.target.getCenter();

    // Save viewport for persistence (restore on next visit)
    saveViewport(center.lat, center.lng, zoom);

    // Clear any pending requests before making new ones
    requestManager.cancelAllRequests();

    // Batch all queries together to avoid multiple renders
    const queries = [];

    if (overlayVisibility.soils && zoom > 10.6) {
      queries.push(
        import('./soil-assay/AssayOverlayOptimized').then(module => 
          module.debouncedQueryAssayWFS(wfsbbox, setAssayGeojson, assayFilters)
        )
      );
    }

    if (overlayVisibility.tenements && zoom > 8) {
      queries.push(
        import('./tenements/TenementOverlayOptimized').then(module =>
          module.debouncedQueryTenementWFS(wfsbbox, tenementFilters, setTenementGeojson)
        )
      );
    }

    if (overlayVisibility.minedex && zoom > 10.6) {
      queries.push(
        import('./minedex/MinedexOverlay').then(module =>
          module.queryMinedexWFSLibre(wfsbbox, setMinedexGeoJson, minedexFilters)
        )
      );
    }

    if (overlayVisibility.dh && zoom > 10.6) {
      queries.push(
        import('./drill-hole/DrillHoleOverlay').then(module =>
          module.queryDHWFS(wfsbbox, setDrillHoleGeoJson, drillHolefilters)
        )
      );
    }

    if (overlayVisibility.graticles) {
      queries.push(
        import('./graticles/GraticlesOverlay').then(module =>
          module.queryGraticlesWFSLibre(wfsbbox, setGraticleGeoJson)
        )
      );
    }

    // Execute all queries concurrently
    Promise.allSettled(queries).then(results => {
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          console.warn(`Query ${index} failed:`, result.reason);
        }
      });
    });

  }, 250), [assayFilters, overlayVisibility, tenementFilters, minedexFilters, drillHolefilters, is40ESideBarShown, saveViewport]);

  // Keep existing utility functions
  const createSquareImg = (r, g, b, w) => {
    const width = w;
    const bytesPerPixel = 4;
    const data = new Uint8Array(width * width * bytesPerPixel);

    for (let x = 0; x < width; x++) {
      for (let y = 0; y < width; y++) {
        const offset = (y * width + x) * bytesPerPixel;
        data[offset + 0] = r;
        data[offset + 1] = g;
        data[offset + 2] = b;
        data[offset + 3] = 255;
      }
    }
    return { width, height: width, data };
  };

  const createTriangleImg = (r, g, b, w) => {
    const width = w;
    const bytesPerPixel = 4;
    const data = new Uint8Array(width * width * bytesPerPixel);

    for (let x = 0; x < width; x++) {
      for (let y = 0; y < width; y++) {
        const offset = (y * width + x) * bytesPerPixel;
        data[offset + 0] = r;
        data[offset + 1] = g;
        data[offset + 2] = b;
        data[offset + 3] = Math.abs(x - width) <= y ? 255 : 0;
      }
    }
    return { width, height: width, data };
  };

  // Keep existing map load handler
  const handleOnLoad = useCallback(e => {
    // Keep existing implementation
    const minedexIconW = 11;
    e.target.addImage('minedex-alumina-icon', createSquareImg(255, 0, 0, minedexIconW));
    e.target.addImage('minedex-iron-icon', createSquareImg(0, 0, 255, minedexIconW));
    e.target.addImage('minedex-precious-metal-icon', createSquareImg(255, 255, 0, minedexIconW));
    
    setMapController(createMapLibreGlMapController(e.target, maplibregl));
  }, []);

  // Keep existing mouse move handler
  const handleMouseMove = useCallback(e => {
    const point = utm.convertLatLngToUtm(e.lngLat.lat, e.lngLat.lng, 2);
    setMousePoint(`${point.ZoneNumber}${point.ZoneLetter} ${point.Easting}mE ${point.Northing}mN`);
  }, []);

  // Keep existing MouseLocationControl
  function MouseLocationControl(props) {
    const controlRef = React.useRef(null);

    class HelloWorldControl {
      onAdd(map) {
        this._map = map;
        this._container = document.createElement('div');
        this._container.className = 'maplibregl-ctrl maplibregl-ctrl-attrib maplibregl-compact maplibregl-compact-show libre-hide-mouse-view';
        this._container.style = `padding: 2px 10px; margin-right: ${isSideBarShown ? '20vw' : '0'};`;
        this._container.textContent = mousePoint;
        controlRef.current = this._container;
        return this._container;
      }

      onRemove() {
        this._container.parentNode.removeChild(this._container);
        this._map = undefined;
        controlRef.current = null;
      }
    }

    useControl(() => new HelloWorldControl(), { position: "bottom-right" });

    // Update the text content whenever mousePoint changes
    React.useEffect(() => {
      if (controlRef.current) {
        controlRef.current.textContent = mousePoint;
      }
    }, [mousePoint]);

    // Update margin when sidebar visibility changes
    React.useEffect(() => {
      if (controlRef.current) {
        controlRef.current.style.marginRight = isSideBarShown ? '20vw' : '0';
      }
    }, [isSideBarShown]);

    return null;
  }

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      requestManager.cancelAllRequests();
      wfsCache.clear();
    };
  }, []);

  // Show loading state while restoring viewport
  if (isViewportLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading map...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <MapProvider>
        <Collapse in={mobileFiltersOpen}>
          <div id="mobile-filter-collapse" className='border-bottom' style={{
            fontSize: 12,
            maxHeight: '60vh',
            overflowY: 'auto',
            backgroundColor: 'white',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1050,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}>
            <SideBar
              setTenementFilters={setTenementFilters}
              tenementFilters={tenementFilters}
              layerVisibility={layerVisibility}
              setLayerVisibility={setLayerVisibility}
              overlayVisibility={overlayVisibility}
              setOverlayVisisbility={setOverlayVisisbility}
              setTenementPopup={setTenementPopup}
              setShowTenementPopup={setShowTenementPopup}
              assayMarkerColourSettings={assayMarkerColourSettings}
              setAssayMarkerColourSettings={setAssayMarkerColourSettings}
              assayFilters={assayFilters}
              setAssayFilters={setAssayFilters}
              assayGeoJson={assayGeoJson}
              setAssayGeoJson={setAssayGeojson}
              minedexFilters={minedexFilters}
              setMinedexFilters={setMinedexFilters}
              drillHoleFilters={drillHolefilters}
              setDrillHoleFilters={setDrillHoleFilters}
              drillHoleColourSettings={drillHoleMarkerColourSettings}
              setDrilHoleColourSettings={setDrillHoleMarkerColourSettings}
              drillHoleGeoJson={drillHoleGeoJson}
              trigger40EWizard={trigger40EWizard}
            />
          </div>
        </Collapse>

        <div className='goldfinder-libre-map'>
            <AlertList onDismiss={onDismissed} alerts={alerts} position="bottom-left" timeout={2000}
              classes={{
                close: classNames("btn-close", "no-text"),
                dismissable: classNames("d-flex", "align-items-start", "flex-row-reverse")
              }}
            />

            <Map
              onLoad={handleOnLoad}
              onMoveEnd={handleMapMoveEnd}
              id='goldfindermap'
              initialViewState={initialViewState}
              mapStyle={goldfinderstyle}
              className=""
              cursor='auto'
              onMouseMove={handleMouseMove}
              onClick={onMapClick}
              attributionControl={false}
              interactiveLayerIds={[
                'live-tenements',
                'pending-tenements',
                'selected-tenement-fill',
                'amalgamations-pending-fill',
                'assay-background-config',
                'alumina-layer',
                'iron-layer',
                'precious-metal-layer',
                'minedex-specialty-metal-layer',
                'steel-alloy-layer',
                'minedex-construction-material-layer',
                'base-metal-layer',
                'minedex-energy-layer',
                'minedex-industrial-metal-layer',
                'minedex-precious-mineral-layer',
                'graticle-fill',
                'section19-fill',
                'dead-tenements',
                'dead-spl-tenements',
                'dhsamples-background-config',
                ...drillHoleMarkerColourSettings.map(setting => `${setting.id}-dh`)
              ]}
            >
              <GeolocateControl trackUserLocation={true} positionOptions={{ enableHighAccuracy: true, timeout: 2000 }} position="top-left" />
              <FullscreenControl position="top-left" />
              <NavigationControl position="top-left" />
              <DrawControl drawCtrl={drawCtrl} drawpointMode={drawpointMode} overlayVisibility={overlayVisibility} />
              {mobileFiltersOpen ? <></> : <MouseLocationControl />}
              <ScaleControl />

              {/* Base layers */}
              <Source id='street' type='raster' scheme='xyz' tileSize={256} attribution={"&copy; Google Maps"} tiles={["https://mt0.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"]}>
                <Layer id='street' layout={{ visibility: `${layerVisibility.street ? "visible" : "none"}` }} source='street' type='raster' />
              </Source>
              <Source id='hybrid' type='raster' scheme='xyz' tileSize={256} attribution={"&copy; Google Maps"} tiles={["https://mt1.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}"]}>
                <Layer id='hybrid' layout={{ visibility: `${layerVisibility.hybrid ? "visible" : "none"}` }} source='hybrid' type='raster' />
              </Source>
              <Source id='topo' type='raster' scheme='xyz' tileSize={256} attribution={'© Commonwealth of Australia (Geoscience Australia) 2020'} tiles={["https://services.ga.gov.au/gis/rest/services/NationalBaseMap/MapServer/tile/{z}/{y}/{x}"]}>
                <Layer id='topo' layout={{ visibility: `${layerVisibility.topo ? "visible" : "none"}` }} source='topo' type='raster' />
              </Source>
              <Source id='geo100' scheme='tms' tileSize={256} attribution={'© State of Western Australia (Department of Energy, Mines, Industry Regulation and Safety) 2025'} type='raster' tiles={["	https://services.slip.wa.gov.au/public/services/SLIP_Public_Services/DMIRS_Imagery_Service/MapServer/WMSServer?service=WMS&request=GetMap&layers=13&styles=&format=image/jpeg&transparent=false&version=1.1.1&width=256&height=256&srs=EPSG:3857&bbox={bbox-epsg-3857}"]}>               
               <Layer id='geo100' layout={{ visibility: `${layerVisibility.geo100 ? "visible" : "none"}` }} source='geo100' type='raster' />
              </Source>
              <Source id='geo200' scheme='tms' tileSize={256} attribution={'© State of Western Australia (Department of Energy, Mines, Industry Regulation and Safety) 2025'} type='raster' tiles={["	https://services.slip.wa.gov.au/public/services/SLIP_Public_Services/DMIRS_Imagery_Service/MapServer/WMSServer?service=WMS&request=GetMap&layers=12&styles=&format=image/jpeg&transparent=false&version=1.1.1&width=256&height=256&srs=EPSG:3857&bbox={bbox-epsg-3857}"]}>                
              <Layer id='geo200' layout={{ visibility: `${layerVisibility.geo200 ? "visible" : "none"}` }} source='geo200' type='raster' />
              </Source>
              <Source id='bedrock' scheme='tms' tileSize={256} attribution={'© State of Western Australia (Department of Energy, Mines, Industry Regulation and Safety) 2025'} type='raster' tiles={["	https://public-services.slip.wa.gov.au/public/services/SLIP_Public_Services/Geology_and_Soils_Map/MapServer/WMSServer?service=WMS&request=GetMap&layers=15&styles=&format=image/jpeg&transparent=false&version=1.1.1&width=256&height=256&srs=EPSG:3857&bbox={bbox-epsg-3857}"]}>
              <Layer id='bedrock' layout={{ visibility: `${layerVisibility.bedrock ? "visible" : "none"}` }} source='bedrock' type='raster' />
              </Source>
              <Source id='magnetic' scheme='tms' tileSize={256} attribution={'© State of Western Australia (Department of Energy, Mines, Industry Regulation and Safety) 2025'} type='raster' tiles={["https://services.slip.wa.gov.au/public/services/SLIP_Public_Services/DMIRS_Imagery_Service/MapServer/WMSServer?service=WMS&request=GetMap&layers=6&styles=&format=image/jpeg&transparent=false&version=1.1.1&width=256&height=256&srs=EPSG:3857&bbox={bbox-epsg-3857}"]}>
              <Layer id='magnetic' layout={{ visibility: `${layerVisibility.magnetic ? "visible" : "none"}` }} source='magnetic' type='raster' />
              </Source>

              <AttributionControl compact={true} position='bottom-left' />

              {/* Optimized overlay components */}
              <TenementOverlayMapLibreOptimized
                tenementFilters={tenementFilters}
                overlayVisibility={overlayVisibility}
                selectedTenement={selectedTenement}
                geoJson={tenementGeoJson}
                setGeojson={setTenementGeojson} />

              <AssayOverlayMapLibreOptimized
                overlayVisibility={overlayVisibility}
                assayMarkerColourSettings={assayMarkerColourSettings}
                assayGeoJson={assayGeoJson}
                setAssayGeojson={setAssayGeojson}
                assayFilters={assayFilters} />

              <MinedexOverlayMapLibre
                overlayVisibility={overlayVisibility}
                minedexGeoJson={minedexGeoJson}
                setMinedexGeojson={setMinedexGeoJson}
                minedexFilters={minedexFilters} />

              <DrillHoleOverlayMapLibreOptimized
                drillHoleMarkerColourSettings={drillHoleMarkerColourSettings}
                overlayVisibility={overlayVisibility}
                drillHoleGeoJson={drillHoleGeoJson}
                setDrillHoleGeoJson={setDrillHoleGeoJson}
                drillHoleFilters={drillHolefilters} />

              <GraticlesOverlayLibre
                graticlesGeoJSON={graticleGeoJson}
                overlayVisibility={overlayVisibility}
                selectedGraticleStack={selectedGraticleStack} />

              <Section19OverlayLibre
                overlayVisibility={overlayVisibility} />

              <AmalgamationsPendingOverlay
                overlayVisibility={overlayVisibility}
                selectedAmalgamation={selectedAmalgamation}
                geoJson={amalgamationsPendingGeoJson}
                setGeoJson={setAmalgamationsPendingGeoJson} />

              {/* Popups */}
              {showTenementPopup ? tenementPopup : <></>}
              {showAssayPopup ? assayPopup : <></>}
              {showMinedexPopup ? minedexPopup : <></>}
              {showGraticlePopup ? graticlePopup : <></>}
              {showSection19Popup ? section19Popup : <></>}
              {showDrawPointPopup ? drawPointPopup : <></>}
              {showDrillHolePopup ? drillHolePopup : <></>}

              <div className="geocoding" style={{ top: 16, left: 16, position: 'absolute' }}>
                <GeocodingControl
                  bbox={[112.565918, -35.353216, 128.957520, -13.154376]}
                  apiKey={API_KEY}
                  mapController={mapController}
                  collapsed={isMobile}
                />
              </div>

              {/* Floating Controls */}
              <FloatingMenu onOpenAccountSettings={onOpenAccountSettings} />
              {!is40ESideBarShown && (
                <SidebarToggle
                  isSideBarShown={isSideBarShown}
                  onToggle={onToggleSidebar}
                />
              )}
            </Map>

            {/* Mobile Layers Toggle - positioned in top-right of map area */}
            <MobileSidebarToggle
              isOpen={mobileFiltersOpen}
              onToggle={onMobileFiltersToggle}
            />
          </div>

          <div className={isSideBarShown ? '' : ' active do-not-render-this'} id="sidebar">
            {!mobileFiltersOpen && <SideBar
              setTenementFilters={setTenementFilters}
              tenementFilters={tenementFilters}
              layerVisibility={layerVisibility}
              setLayerVisibility={setLayerVisibility}
              overlayVisibility={overlayVisibility}
              setOverlayVisisbility={setOverlayVisisbility}
              setTenementPopup={setTenementPopup}
              setShowTenementPopup={setShowTenementPopup}
              assayMarkerColourSettings={assayMarkerColourSettings}
              setAssayMarkerColourSettings={setAssayMarkerColourSettings}
              assayFilters={assayFilters}
              setAssayFilters={setAssayFilters}
              assayGeoJson={assayGeoJson}
              setAssayGeoJson={setAssayGeojson}
              minedexFilters={minedexFilters}
              setMinedexFilters={setMinedexFilters}
              drillHoleFilters={drillHolefilters}
              setDrillHoleFilters={setDrillHoleFilters}
              drillHoleColourSettings={drillHoleMarkerColourSettings}
              setDrilHoleColourSettings={setDrillHoleMarkerColourSettings}
              drillHoleGeoJson={drillHoleGeoJson}
              trigger40EWizard={trigger40EWizard}
              presetsControl={
                <PresetsControl
                  presets={presets}
                  activePresetId={activePresetId}
                  setActivePresetId={setActivePresetId}
                  isLoading={isPresetsLoading}
                  userLoggedIn={presetsUserLoggedIn}
                  canSaveMore={canSaveMore}
                  userPresetCount={userPresetCount}
                  maxUserPresets={maxUserPresets}
                  buildCurrentPayload={buildCurrentPayload}
                  savePresetAsNew={savePresetAsNew}
                  updatePreset={updatePreset}
                  renamePreset={renamePreset}
                  deletePreset={deletePreset}
                  onError={onPresetError}
                />
              }
            />}
          </div>

          <div className={is40ESideBarShown ? 'tab-content' : 'tab-content active do-not-render-this'} id="sidebar40e">
            <SideBar40E
              setIs40ESideBarShown={setIs40ESideBarShown}
              is40ESideBarShown={is40ESideBarShown}
              tenement40E={tenement40E}
              setOverlayVisisbility={setOverlayVisisbility}
              overlayVisibility={overlayVisibility}
              selectedGraticleStack={selectedGraticleStack}
              setSelectedGraticuleStack={setSelectedGraticuleStack}
              setAlerts={setAlerts}
              setSideBarShown={setSideBarShown}
            />
          </div>

        <SampleReporter reportedAssay={reportedAssay} show={reportAssayModalShow} onHide={() => setReportAssayModalShow(false)} />
        <DHSampleReporter reportedAssay={reportedDh} show={reportDhModalShow} onHide={() => setReportDhModalShow(false)} />
        
        {/* 40E Confirmation Modal */}
        <Modal show={show40EConfirmModal} onHide={cancel40EWizard} centered>
          <Modal.Header closeButton>
            <Modal.Title>Create Section 40E Permit</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Are you sure you want to create a Section 40E permit for tenement <strong>{pendingTenement40E?.properties?.fmt_tenid}</strong>?</p>
            <p>This will open the permit wizard and enable graticule selection mode.</p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={cancel40EWizard}>
              Cancel
            </Button>
            <Button variant="primary" onClick={confirm40EWizard}>
              Create Permit
            </Button>
          </Modal.Footer>
        </Modal>
      </MapProvider>
    </>
  );
}